// src/utils/scheduleChanges.js
//
// Scheduling pilot (Pass 42) — canonical schedule = `bookings`.
//
// This module is the single place that (a) appends an immutable audit record to
// the `scheduleChanges` collection and (b) notifies the affected instructor(s)
// whenever a material schedule change happens. It uses the CANONICAL notification
// system (`createNotification` in notifications.js) — it does not create a parallel
// notification flow. Every booking mutator that changes the schedule should route
// through the `recordAndNotify*` helpers here so that history + notification stay
// in lockstep and can never drift.

import { addDoc, collection, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { PLATFORM_COLLECTIONS } from './platformSchema';
import { createNotification } from './notifications';
import { isCanonicalMemberId } from './instructorCategory';

/**
 * Deterministic notification id for a schedule change. Re-processing the SAME
 * logical event (same scheduleChangeId) yields the SAME id, so setDoc overwrites
 * rather than duplicating — robust to retries and delayed duplicate writes.
 * Genuinely separate changes have different scheduleChangeIds → different ids.
 */
export function buildNotificationId(bookingId, scheduleChangeId, instructorId, changeType) {
  return `sch_${bookingId}_${scheduleChangeId}_${instructorId}_${changeType}`
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .slice(0, 400);
}

export const SCHEDULE_CHANGE_TYPES = {
  CREATED: 'created',
  TIME_CHANGED: 'time_changed',
  DATE_CHANGED: 'date_changed',
  LOCATION_CHANGED: 'location_changed',
  NOTES_CHANGED: 'notes_changed',
  ASSIGNED: 'assigned',
  REASSIGNED: 'reassigned',
  UNASSIGNED: 'unassigned',
  CANCELED: 'canceled',
  RESTORED: 'restored',
};

// Fields whose change is "material" and worth an audit entry + instructor notice.
const TRACKED_FIELDS = [
  { field: 'startAt', label: 'Start time', kind: 'time' },
  { field: 'endAt', label: 'Finish time', kind: 'time' },
  { field: 'locationLabel', label: 'Meeting location', kind: 'text' },
  { field: 'meetingType', label: 'Meeting type', kind: 'text' },
  { field: 'notes', label: 'Operational notes', kind: 'text' },
];

function toDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value?.seconds) return new Date(value.seconds * 1000);
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toIso(value) {
  const d = toDate(value);
  return d ? d.toISOString() : '';
}

export function toIsoDateKey(value) {
  const d = toDate(value);
  if (!d) return '';
  // Local YYYY-MM-DD (schedule days are operational-local, not UTC).
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fmtTime(value) {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Deep link to the affected instructor's schedule for the affected day.
export function buildScheduleLink(booking = {}) {
  const dateKey = toIsoDateKey(booking.startAt);
  const bookingId = booking.bookingId || booking.id || '';
  const params = [];
  if (dateKey) params.push(`date=${dateKey}`);
  if (bookingId) params.push(`booking=${bookingId}`);
  return `/instructor/schedule${params.length ? `?${params.join('&')}` : ''}`;
}

/**
 * Compute the material differences between two normalized booking objects.
 * Returns an array of { field, label, kind, previousValue, newValue } for the
 * TRACKED_FIELDS that actually changed. Time fields are compared by millisecond.
 */
export function computeBookingScheduleDiff(previousBooking = {}, nextBooking = {}) {
  const changes = [];
  for (const spec of TRACKED_FIELDS) {
    const prev = previousBooking[spec.field];
    const next = nextBooking[spec.field];
    if (spec.kind === 'time') {
      const prevMs = toDate(prev)?.getTime() ?? null;
      const nextMs = toDate(next)?.getTime() ?? null;
      if (prevMs !== nextMs) {
        changes.push({
          field: spec.field,
          label: spec.label,
          kind: 'time',
          previousValue: toIso(prev),
          newValue: toIso(next),
        });
      }
    } else {
      const prevStr = String(prev ?? '').trim();
      const nextStr = String(next ?? '').trim();
      if (prevStr !== nextStr) {
        changes.push({
          field: spec.field,
          label: spec.label,
          kind: spec.kind,
          previousValue: prevStr,
          newValue: nextStr,
        });
      }
    }
  }
  return changes;
}

// Did the day (calendar date) change, vs just the time within a day?
function isDateChange(changes) {
  const startChange = changes.find((c) => c.field === 'startAt');
  if (!startChange) return false;
  return toIsoDateKey(startChange.previousValue) !== toIsoDateKey(startChange.newValue);
}

/**
 * Low-level append to the immutable `scheduleChanges` audit collection.
 * Never updates or deletes — always a new event doc.
 */
export async function recordScheduleChange(
  db,
  {
    bookingId = '',
    organizationId = '',
    changeType = 'updated',
    changes = [],
    previousInstructorId = '',
    newInstructorId = '',
    affectedInstructorIds = [],
    changedByUserId = '',
    changedByName = '',
    notificationSent = false,
    notificationIds = [],
    source = 'app',
  } = {}
) {
  if (!bookingId) return null;
  const payload = {
    bookingId,
    organizationId,
    changeType,
    changes,
    previousInstructorId: previousInstructorId || '',
    newInstructorId: newInstructorId || '',
    affectedInstructorIds: Array.from(new Set((affectedInstructorIds || []).filter(Boolean))),
    changedByUserId,
    changedByName,
    notificationSent: Boolean(notificationSent),
    notificationIds: notificationIds || [],
    source,
    createdAt: serverTimestamp(),
  };
  const ref = await addDoc(collection(db, PLATFORM_COLLECTIONS.SCHEDULE_CHANGES), payload);
  return { scheduleChangeId: ref?.id || '', ...payload };
}

function buildChangeSummary(booking, changeType, changes) {
  const when = fmtTime(booking.startAt);
  const dateKey = toIsoDateKey(booking.startAt);
  const who = booking.requesterName ? ` with ${booking.requesterName}` : '';
  switch (changeType) {
    case SCHEDULE_CHANGE_TYPES.CANCELED:
      return `Your ${dateKey} ${when} lesson${who} has been cancelled.`;
    case SCHEDULE_CHANGE_TYPES.RESTORED:
      return `Your ${dateKey} ${when} lesson${who} has been restored.`;
    case SCHEDULE_CHANGE_TYPES.ASSIGNED:
      return `New lesson assigned: ${dateKey} at ${when}${who}.`;
    case SCHEDULE_CHANGE_TYPES.UNASSIGNED:
    case SCHEDULE_CHANGE_TYPES.REASSIGNED:
      return `A lesson has been removed from your schedule (${dateKey} ${when}${who}).`;
    default: {
      const parts = (changes || []).map((c) => {
        if (c.kind === 'time') return `${c.label.toLowerCase()} → ${fmtTime(c.newValue)}`;
        return `${c.label.toLowerCase()} updated`;
      });
      const detail = parts.length ? ` (${parts.join(', ')})` : '';
      return `Your ${dateKey} ${when} lesson${who} has changed${detail}.`;
    }
  }
}

function titleForChange(changeType) {
  switch (changeType) {
    case SCHEDULE_CHANGE_TYPES.CANCELED: return 'Lesson cancelled';
    case SCHEDULE_CHANGE_TYPES.RESTORED: return 'Lesson restored';
    case SCHEDULE_CHANGE_TYPES.ASSIGNED: return 'New lesson assigned';
    case SCHEDULE_CHANGE_TYPES.REASSIGNED:
    case SCHEDULE_CHANGE_TYPES.UNASSIGNED: return 'Lesson removed from your schedule';
    case SCHEDULE_CHANGE_TYPES.DATE_CHANGED: return 'Lesson date changed';
    case SCHEDULE_CHANGE_TYPES.TIME_CHANGED: return 'Lesson time changed';
    case SCHEDULE_CHANGE_TYPES.LOCATION_CHANGED: return 'Meeting location changed';
    default: return 'Schedule updated';
  }
}

/**
 * Notify a single instructor about a schedule change to one of their lessons.
 * Uses the canonical createNotification (which has a 30s dedup guard on
 * userId:type:relatedEntityId, so double-fires for the same change collapse).
 */
export async function notifyInstructorScheduleChange(
  db,
  { instructorId = '', recipientAuthUserId = '', booking = {}, changeType = 'updated', changes = [], scheduleChangeId = '', actorUserId = '', actorName = '' } = {}
) {
  if (!instructorId) return null;
  const bookingId = booking.bookingId || booking.id || '';
  // Pass 49A §5: recipient is the linked Firebase Auth UID when available. A CANONICAL
  // member id (seed_instructor_*) is NOT an Auth user — with no link we SUPPRESS the
  // notification (the scheduleChange audit still exists upstream) rather than create an
  // orphan. Legacy compatibility: a non-canonical instructorId that genuinely IS an Auth
  // UID still receives the notification.
  const looksCanonical = isCanonicalMemberId(instructorId); // §10: isolated legacy heuristic
  const recipient = recipientAuthUserId || (looksCanonical ? '' : instructorId);
  if (!recipient) return { id: '', delivered: false, reason: 'instructor_not_auth_linked', canonicalInstructorId: instructorId };
  const deliverable = !!recipientAuthUserId || !looksCanonical;
  // Deterministic id when we have a scheduleChangeId (the normal path); falls
  // back to auto-id + time-window guard only if no audit id is available.
  const notificationId = scheduleChangeId
    ? buildNotificationId(bookingId, scheduleChangeId, recipient, changeType)
    : '';
  return createNotification(db, {
    userId: recipient,
    canonicalInstructorId: instructorId,
    deliverable,
    organizationId: booking.organizationId || '',
    audience: 'organization_staff', // Pass 85.1B.3 — schedule change delivered to the instructor (staff)
    type: 'schedule_changed',
    title: titleForChange(changeType),
    message: buildChangeSummary(booking, changeType, changes),
    link: buildScheduleLink(booking),
    actorUserId,
    actorName,
    relatedEntityType: 'booking',
    relatedEntityId: bookingId,
    metadata: { changeType, scheduleChangeId },
    notificationId,
  });
}

// Persist the notification ids back onto the audit event so history + delivery
// stay consistent.
async function stampNotificationIds(db, scheduleChangeId, notificationIds) {
  if (!scheduleChangeId || !notificationIds.length) return;
  try {
    await updateDoc(doc(db, PLATFORM_COLLECTIONS.SCHEDULE_CHANGES, scheduleChangeId), {
      notificationSent: true,
      notificationIds,
    });
  } catch (err) {
    // best-effort; the notifications themselves already succeeded
  }
}

/**
 * updateBooking edit → audit + notify the currently-assigned instructor.
 * Returns { changed, scheduleChangeId, notified }.
 */
export async function recordAndNotifyBookingEdit(
  db,
  { previousBooking = {}, nextBooking = {}, actorUserId = '', actorName = '', source = 'app' } = {}
) {
  const changes = computeBookingScheduleDiff(previousBooking, nextBooking);
  if (!changes.length) return { changed: false, scheduleChangeId: '', notified: false };

  let changeType = SCHEDULE_CHANGE_TYPES.NOTES_CHANGED;
  if (changes.some((c) => c.field === 'locationLabel')) changeType = SCHEDULE_CHANGE_TYPES.LOCATION_CHANGED;
  if (changes.some((c) => c.field === 'startAt' || c.field === 'endAt')) {
    changeType = isDateChange(changes) ? SCHEDULE_CHANGE_TYPES.DATE_CHANGED : SCHEDULE_CHANGE_TYPES.TIME_CHANGED;
  }

  const instructorId = nextBooking.instructorId || previousBooking.instructorId || '';

  // Audit first so the notification id can be derived deterministically from it.
  const audit = await recordScheduleChange(db, {
    bookingId: nextBooking.bookingId || nextBooking.id,
    organizationId: nextBooking.organizationId,
    changeType,
    changes,
    affectedInstructorIds: [instructorId],
    changedByUserId: actorUserId,
    changedByName: actorName,
    notificationSent: false,
    notificationIds: [],
    source,
  });
  const scId = audit?.scheduleChangeId || '';

  const notificationIds = [];
  if (instructorId) {
    const notif = await notifyInstructorScheduleChange(db, {
      instructorId, recipientAuthUserId: nextBooking.instructorAuthUserId || '', // Pass 49A §4
      booking: nextBooking, changeType, changes, scheduleChangeId: scId, actorUserId, actorName,
    }).catch(() => null);
    if (notif?.id) notificationIds.push(notif.id);
  }
  await stampNotificationIds(db, scId, notificationIds);

  return { changed: true, scheduleChangeId: scId, notified: notificationIds.length > 0 };
}

/**
 * cancelBooking / restoreBooking → audit + notify the assigned instructor.
 */
export async function recordAndNotifyStatusChange(
  db,
  { booking = {}, changeType = SCHEDULE_CHANGE_TYPES.CANCELED, actorUserId = '', actorName = '', source = 'app' } = {}
) {
  const instructorId = booking.instructorId || '';
  const audit = await recordScheduleChange(db, {
    bookingId: booking.bookingId || booking.id,
    organizationId: booking.organizationId,
    changeType,
    changes: [{ field: 'status', label: 'Status', kind: 'text', previousValue: '', newValue: changeType }],
    affectedInstructorIds: [instructorId],
    changedByUserId: actorUserId,
    changedByName: actorName,
    notificationSent: false,
    notificationIds: [],
    source,
  });
  const scId = audit?.scheduleChangeId || '';

  const notificationIds = [];
  if (instructorId) {
    const notif = await notifyInstructorScheduleChange(db, {
      instructorId, recipientAuthUserId: booking.instructorAuthUserId || '', // Pass 49A §4 (cancel/restore)
      booking, changeType, scheduleChangeId: scId, actorUserId, actorName,
    }).catch(() => null);
    if (notif?.id) notificationIds.push(notif.id);
  }
  await stampNotificationIds(db, scId, notificationIds);
  return { scheduleChangeId: scId, notified: notificationIds.length > 0 };
}

/**
 * assignInstructorToBooking / unassignBooking → audit + notify BOTH the new
 * instructor (assigned) and the previous instructor (removed). The client
 * notification is handled separately by the booking layer.
 */
export async function recordAndNotifyReassignment(
  db,
  { booking = {}, previousInstructorId = '', newInstructorId = '', newInstructorAuthUserId = '', previousInstructorAuthUserId = '', actorUserId = '', actorName = '', source = 'app' } = {}
) {
  const affected = [];
  if (newInstructorId) affected.push(newInstructorId);
  if (previousInstructorId && previousInstructorId !== newInstructorId) affected.push(previousInstructorId);

  const changeType = newInstructorId
    ? (previousInstructorId ? SCHEDULE_CHANGE_TYPES.REASSIGNED : SCHEDULE_CHANGE_TYPES.ASSIGNED)
    : SCHEDULE_CHANGE_TYPES.UNASSIGNED;

  // Audit first, then derive deterministic notification ids from scheduleChangeId.
  const audit = await recordScheduleChange(db, {
    bookingId: booking.bookingId || booking.id,
    organizationId: booking.organizationId,
    changeType,
    changes: [{
      field: 'instructorId',
      label: 'Assigned instructor',
      kind: 'text',
      previousValue: previousInstructorId || '',
      newValue: newInstructorId || '',
    }],
    previousInstructorId,
    newInstructorId,
    affectedInstructorIds: affected,
    changedByUserId: actorUserId,
    changedByName: actorName,
    notificationSent: false,
    notificationIds: [],
    source,
  });
  const scId = audit?.scheduleChangeId || '';
  const notificationIds = [];

  // New instructor gains the lesson (one notification).
  if (newInstructorId) {
    const notif = await notifyInstructorScheduleChange(db, {
      instructorId: newInstructorId,
      recipientAuthUserId: newInstructorAuthUserId || booking.instructorAuthUserId || '',
      booking: { ...booking, instructorId: newInstructorId },
      changeType: SCHEDULE_CHANGE_TYPES.ASSIGNED,
      scheduleChangeId: scId, actorUserId, actorName,
    }).catch(() => null);
    if (notif?.id) notificationIds.push(notif.id);
  }
  // Previous instructor loses the lesson (one notification).
  if (previousInstructorId && previousInstructorId !== newInstructorId) {
    const notif = await notifyInstructorScheduleChange(db, {
      instructorId: previousInstructorId,
      recipientAuthUserId: previousInstructorAuthUserId || '',
      booking: { ...booking, instructorId: previousInstructorId },
      changeType: newInstructorId ? SCHEDULE_CHANGE_TYPES.REASSIGNED : SCHEDULE_CHANGE_TYPES.UNASSIGNED,
      scheduleChangeId: scId, actorUserId, actorName,
    }).catch(() => null);
    if (notif?.id) notificationIds.push(notif.id);
  }
  await stampNotificationIds(db, scId, notificationIds);

  return { scheduleChangeId: scId, notified: notificationIds.length > 0 };
}
